import { TeliaImage, TeliaLinkImage } from '@teliads/components/react';

interface Props {
  imgSrc: string;
  alt: string;
  hyperlink: string | undefined;
  description: string;
  imageSize?: keyof typeof sizeMap;
}

const sizeMap = {
  'icon-small': { w: 64, h: 64 },
  'icon-medium': { w: 128, h: 128 },
  'logo-wide': { w: 300, h: 100 },
  'logo-extra-wide': { w: 400, h: 100 },
  'logo-square': { w: 200, h: 200 },
  'square-large': { w: 400, h: 400 },
  'logo-tall': { w: 150, h: 300 },
  'portrait-sm': { w: 150, h: 225 },
  'portrait-md': { w: 300, h: 450 },
  'portrait-lg': { w: 450, h: 675 },
  'banner-sm': { w: 600, h: 200 },
};

const PHONE_NUMBER_STRING = 'phone number';

const getImageUrlWithSizing = (url: string, imageSize?: keyof typeof sizeMap): string => {
  if (!imageSize || !sizeMap[imageSize]) {
    return url;
  }

  const { w, h } = sizeMap[imageSize];
  const urlObj = new URL(url);
  urlObj.searchParams.set('w', w.toString());
  urlObj.searchParams.set('h', h.toString());

  return urlObj.toString();
};

export default function ImageAssetContainer({
  imgSrc,
  alt,
  hyperlink,
  description,
  imageSize,
}: Readonly<Props>) {
  const containsPhoneNumber = alt.toLowerCase().includes(PHONE_NUMBER_STRING);
  const displayAlt = alt.split(' ').slice(0, 2).join(' ');
  const phoneNumberHref = `tel:${description}`;
  const imageClassName = containsPhoneNumber
    ? 'richtext-element__phone-number'
    : 'richtext-element';
  const imageHyperlink = containsPhoneNumber ? phoneNumberHref : hyperlink;

  const imageStyle = imageSize
    ? { height: sizeMap[imageSize]?.h, width: sizeMap[imageSize]?.w }
    : undefined;

  const isSvg = imgSrc.split('?')[0].endsWith('.svg');
  const optimizedImgSrc = isSvg ? imgSrc.split('?')[0] : getImageUrlWithSizing(imgSrc, imageSize);

  return (
    <>
      {hyperlink || containsPhoneNumber ? (
        <TeliaLinkImage
          htmlHref={imageHyperlink}
          linkText={displayAlt}
          className={imageSize ? 'richtext-image-wrapper' : imageClassName}
          dataTestid="richtext-telia-image"
        >
          <TeliaImage
            src={optimizedImgSrc}
            alt={alt}
            variant={imageSize ? undefined : 'scalable'}
            className={imageSize ? 'richtext-element-icon' : imageClassName}
            dataTestid="richtext-telia-image"
            style={imageStyle}
          />
        </TeliaLinkImage>
      ) : (
        <TeliaImage
          src={optimizedImgSrc}
          alt={alt}
          variant={imageSize ? undefined : 'scalable'}
          className={imageSize ? 'richtext-element-icon' : 'richtext-element'}
          dataTestid="richtext-telia-image"
          style={imageStyle}
        />
      )}
    </>
  );
}
